import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faPlus } from "@fortawesome/pro-solid-svg-icons"
import { Box, Container, Grid, MenuItem, Typography } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import IconForButton from "components/ButtonIcon"
import ClpTable from "components/ClpTable"
import PaperBox from "components/PaperBox"
import SubTabs from "components/SubTabs"
import { TabPanel } from "components/navigation/TabPanel"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { UserCustomFieldDto } from "../edit/UserCustomField.interfaces"
import { UserCustomFieldCategorySidebar } from "./UserCustomFieldCategorySidebar"
import { UserCustomFieldSidebar } from "./UserCustomFieldSidebar"
import { getFieldTypeDescription } from "./userCustomField.helpers"
import { UserCustomFieldCategory } from "./userCustomField.interfaces"
import { StyledGreyTypography, StyledPopover } from "./userCustomFields.styled"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { useTranslation } from "react-i18next"

const UserCustomFields = () => {
  const [userCustomFieldsResult, userCustomFieldsApi] = useApi() as [IApiResult, IApiFunc]
  const [userCustomFieldsCategoriesResult, userCustomFieldsCategoriesApi] = useApi() as [IApiResult, IApiFunc]
  const [userCustomFieldsActiveCategoriesResult, userCustomFieldsActiveCategoriesApi] = useApi() as [
    IApiResult,
    IApiFunc
  ]
  const [customerResult, customerApi] = useApi() as [IApiResult, IApiFunc]
  const appContext = useContext(AppContext)
  const [tabValue, setTabValue] = useState(0)
  const [anchorElement, setAnchorElement] = useState<Element | null>(null)
  const [clickedCategory, setClickedCategory] = useState<UserCustomFieldCategory>()
  const [clickedUserCustomField, setClickedUserCustomField] = useState<UserCustomFieldDto>()
  const initialSort = [{ id: "name", desc: false }]
  const { t } = useTranslation()

  const tabData = [
    { index: 0, label: t("global.custom_fields", "Custom fields"), queryParam: "customfields" },
    { index: 1, label: t("global.categories", "Categories"), queryParam: "categories" }
  ]

  useEffect(() => {
    customerApi.get(apiEndpoints.customeradmin.getcustomer)
  }, [])

  useEffect(() => {
    if (customerResult.status !== 2) return
    fetchData()
  }, [customerResult])

  useEffect(() => {
    if (appContext.isSidebarOpen) return
    setClickedCategory(undefined)
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    if (appContext.isSidebarOpen) return
    setClickedUserCustomField(undefined)
  }, [appContext.isSidebarOpen])

  const fetchData = () => {
    const customFieldUrl = apiEndpoints.usercustomfieldsadmin.getusercustomfieldbycustomerid
    userCustomFieldsApi.get(customFieldUrl)
    const categoriesUrl = apiEndpoints.usercustomfieldsadmin.getcustomfieldcategoriesbycustomerid
    userCustomFieldsCategoriesApi.get(categoriesUrl)
    const activeCategoriesUrl = apiEndpoints.usercustomfieldsadmin.getactivecustomfieldcategoriesbycustomerid
    userCustomFieldsActiveCategoriesApi.get(activeCategoriesUrl)
  }

  const customFieldsColumns = useMemo(() => {
    const columns: any = [
      {
        accessorKey: "name",
        header: t("global.name", "Name"),
        cell: ({ row }: any) => (
          <StyledGreyTypography active={row.original?.category?.active}>
            {row.original?.name}
          </StyledGreyTypography>
        )
      },
      {
        accessorKey: "category.name",
        header: t("global.category", "Category"),
        cell: ({ row }: any) => {
          const text = row.original?.category?.active
            ? row.original?.category?.name
            : `${row.original?.category?.name} (${t("global.inactive", "Inactive")})`
          return <StyledGreyTypography active={row.original?.category?.active}>{text}</StyledGreyTypography>
        }
      },
      {
        accessorKey: "types",
        header: t("global.field_type", "Field type"),
        cell: ({ row }: any) => {
          const types = row.original?.valueFields
            ?.map((valueField: any) => {
              return getFieldTypeDescription(valueField.component, valueField.dataType)
            })
            .join(", ")
          return <StyledGreyTypography active={row.original?.category?.active}>{types}</StyledGreyTypography>
        }
      },
      {
        accessorKey: "active",
        header: t("global.active", "Active"),
        cell: ({ row }: any) => {
          const activeText = row.original.active ? t("global.yes", "Yes") : t("global.no", "No")
          return (
            <StyledGreyTypography active={row.original?.category?.active}>{activeText}</StyledGreyTypography>
          )
        }
      }
    ]

    if (hasRole(appContext.appUser, allRoles.CustomerAdmin)) {
      columns.push({
        accessorKey: "edit",
        disablePadding: true,
        header: "",
        align: "right",
        cell: ({ cell }: any) => {
          return (
            <StyledTableButtonGroup>
              <TableButton
                type={TableButtonTypeEnum.Edit}
                handleOnClick={() => {
                  setClickedUserCustomField(cell.row.original)
                  appContext.toggleSideBar("UserCustomFieldForm", true)
                  // setShowCustomFieldSidebar(true)
                }}
              />
            </StyledTableButtonGroup>
          )
        }
      })
    }
    return columns
  }, [])

  const customFieldsCategoriesColumns = useMemo(() => {
    const columns: any = [
      {
        accessorKey: "name",
        header: t("global.name", "Name"),
        cell: ({ row }: any) => <>{row.original.name}</>
      },
      {
        accessorKey: "active",
        header: t("global.active", "Active"),
        cell: ({ row }: any) => <>{row.original.active ? t("global.yes", "Yes") : t("global.no", "No")}</>
      }
    ]

    if (hasRole(appContext.appUser, allRoles.CustomerAdmin)) {
      columns.push({
        accessorKey: "edit",
        disablePadding: true,
        header: "",
        align: "right",
        cell: ({ cell }: any) => {
          return (
            <StyledTableButtonGroup>
              <TableButton
                type={TableButtonTypeEnum.Edit}
                handleOnClick={() => {
                  setClickedCategory(cell.row.original)
                  appContext.toggleSideBar("CustomFieldCategoryForm", true)
                }}
              />
            </StyledTableButtonGroup>
          )
        }
      })
    }
    return columns
  }, [])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorElement(null)
  }
  const open = Boolean(anchorElement)
  const id = open ? "simple-popover" : undefined

  const categoriesExists =
    userCustomFieldsCategoriesResult.status === 2 &&
    userCustomFieldsCategoriesResult.data &&
    userCustomFieldsCategoriesResult.data.length > 0
  const activeCategoriesExists =
    userCustomFieldsActiveCategoriesResult.status === 2 &&
    userCustomFieldsActiveCategoriesResult.data &&
    userCustomFieldsActiveCategoriesResult.data.length > 0
  const userCustomFieldsExists =
    userCustomFieldsResult.status === 2 &&
    userCustomFieldsResult.data &&
    userCustomFieldsResult.data.length > 0
  const loadingData = userCustomFieldsCategoriesResult.status !== 2 || userCustomFieldsResult.status !== 2

  return (
    <Container>
      <UserCustomFieldCategorySidebar
        clickedCategory={clickedCategory}
        handleSubmit={() => fetchData()}
      />
      <UserCustomFieldSidebar
        clickedUserCustomField={clickedUserCustomField}
        handleSubmit={() => fetchData()}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid direction="column">
          <Typography variant="h2" mb={1}>{t("global.custom_fields", "Custom fields")}</Typography>
          <Typography variant="bodySmall">{t("settings.user.set_up_custom_fields", "Set up your own fields.")}</Typography>
        </Grid>
        <Box>
          <Button
            disabled={!categoriesExists}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              handleClick(event)
            }}
          >
            {t("global.add", "Add")}
          </Button>
          <StyledPopover
            id={id}
            open={open}
            anchorEl={anchorElement}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            disableRestoreFocus>
            <MenuItem
              disabled={!activeCategoriesExists}
              onClick={() => {
                appContext.toggleSideBar("UserCustomFieldForm", true)
                handlePopoverClose()
              }}>
              {t("global.custom_fields", "Custom field")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                appContext.toggleSideBar("CustomFieldCategoryForm", true)
                handlePopoverClose()
              }}>
              {t("global.category", "Category")}
            </MenuItem>
          </StyledPopover>
        </Box>
      </Grid>
      <SubTabs tabs={tabData} value={tabValue} setValue={setTabValue} marginLeft={1} />
      <Grid container direction="row">
        <Grid xs={12} md={12} lg={12} pt={{ xs: 2, lg: 0 }}>
          <PaperBox mt={0}>
            <TabPanel value={tabValue} index={0}>
              {loadingData || userCustomFieldsExists ? (
                <>
                  <Box px={{ xs: 2, sm: 3 }} pt={{ xs: 2, sm: 3 }}>
                    <Typography variant="bodySmall">{t("settings.user.here_you_manage_custom_fields_for_user", "Here you manage custom fields for users.")}</Typography>
                    <Typography variant="bodySmall">
                      {t("settings.user.individual_settings_is_handle", "Individual settings are managed on a per-user basis.")}
                    </Typography>
                  </Box>
                  <ClpTable
                    hideFilter
                    hideSearch
                    columns={customFieldsColumns}
                    rows={userCustomFieldsResult.status === 2 && userCustomFieldsResult.data}
                    loadingState={userCustomFieldsResult.status}
                  />
                </>
              ) : loadingData || activeCategoriesExists ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  gap={4}
                >
                  <Typography variant="bodySmall">{t("settings.user.you_need_to_create_custom_field", "You need to create your own fields")}</Typography>
                  <Button
                    color="primary"
                    onClick={() => {
                      appContext.toggleSideBar("UserCustomFieldForm", true)
                    }}
                  >
                    {t("settings.user.create_custom_field", "Create custom field")}
                  </Button>
                </Grid>
              ) : (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  gap={4}
                  sx={{ height: "200px" }}
                >
                  <Typography variant="bodySmall">
                    {t("settings.user.you_need_to_create_categories", "You need to create categories to be able to add your own fields.")}
                  </Typography>
                  <Button
                    color="primary"
                    onClick={() => {
                      appContext.toggleSideBar("CustomFieldCategoryForm", true)
                    }}
                  >
                    {t("global.create_category", "Create category")}
                  </Button>
                </Grid>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {loadingData || categoriesExists ? (
                <>
                  <Box px={{ xs: 2, sm: 3 }} pt={{ xs: 2, sm: 3 }}>
                    <Typography variant="bodySmall">
                      {t("settings.user.here_you_create_global_categories", "Here you create global categories for custom fields for users.")}
                    </Typography>
                    <Typography variant="bodySmall">
                      {t("settings.user.individual_settings_is_handle", "Individual settings are managed on a per-user basis.")}
                    </Typography>
                  </Box>
                  <ClpTable
                    hideFilter
                    hideSearch
                    columns={customFieldsCategoriesColumns}
                    rows={
                      userCustomFieldsCategoriesResult.status === 2 && userCustomFieldsCategoriesResult.data
                    }
                    loadingState={userCustomFieldsCategoriesResult.status}
                  />
                </>
              ) : (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  gap={4}
                  sx={{ height: "200px" }}
                >
                  <Typography variant="bodySmall">{t("settings.user.you_have_not_created_any_categories", "You have not created any categories yet.")}</Typography>
                  <Button
                    color="primary"
                    onClick={() => {
                      appContext.toggleSideBar("CustomFieldCategoryForm", true)
                    }}
                  >
                    {t("global.create_category", "Create category")}
                  </Button>
                </Grid>
              )}
            </TabPanel>
          </PaperBox>
        </Grid>
      </Grid>
    </Container>
  )
}

export default UserCustomFields
